/**
 * Політика відновлення зʼєднання.
 *
 * Переноситься з мобільного клієнта без змін: до 5 спроб, лінійна пауза
 * 2 с × номер спроби, успіх скидає лічильник.
 */
export const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_DELAY_MS = 2000;

export function reconnectDelayMs(attempt: number): number {
  return BASE_DELAY_MS * attempt;
}

/**
 * Guard, що дозволяє рівно одну спробу відновлення одночасно.
 *
 * Джерел сигналу про втрату звʼязку в браузері більше, ніж на мобільному:
 * стан RTCPeerConnection, стан ICE, закриття сокета, `visibilitychange`,
 * `pagehide`, `online`/`offline`. Усі вони сходяться сюди. Прапорець
 * перевіряється ДО інкременту лічильника і ДО першого `await`, а звільняється
 * тільки у `finally` — інакше вікно між перевіркою і встановленням пропускає
 * паралельну спробу.
 */
export class ReconnectGuard {
  private inFlight = false;
  private attempts = 0;
  private exhausted = false;

  get attemptsUsed(): number {
    return this.attempts;
  }

  get isExhausted(): boolean {
    return this.exhausted;
  }

  get isRunning(): boolean {
    return this.inFlight;
  }

  /**
   * Запускає спробу, якщо жодна не триває і бюджет не вичерпано.
   * Повертає `false`, коли сигнал відкинуто — це нормальний шлях, а не помилка.
   */
  async run(attempt: (attemptNumber: number) => Promise<boolean>): Promise<boolean> {
    if (this.inFlight || this.exhausted) return false;
    this.inFlight = true;
    this.attempts += 1;
    const current = this.attempts;

    try {
      const succeeded = await attempt(current);
      if (succeeded) {
        // Успіх повертає повний бюджет: наступна втрата звʼязку не має
        // страждати через попередню.
        this.attempts = 0;
        return true;
      }
      if (current >= MAX_RECONNECT_ATTEMPTS) {
        this.exhausted = true;
      }
      return false;
    } finally {
      this.inFlight = false;
    }
  }

  reset(): void {
    this.inFlight = false;
    this.attempts = 0;
    this.exhausted = false;
  }
}
